<template>
	<div class="grid gap-16">
		<h2 class="fs-22 lh-24 mb-8">Edit Availability</h2>

		<!-- Dates -->
		<div class="px-24 py-20 border rel">
			<div class="flex-top gap-16 mb-8">
				<h2 class="fs-16 lh-20 mb-0">Dates</h2>
				<button v-if="hasGroupedDates" class="btn btn-link align-right" @click="showGroupedDates = !showGroupedDates">
					{{ showGroupedDates ? 'Expand All Dates' : 'Collapse All Dates' }}
				</button>
			</div>
			
			<div v-if="showGroupedDates" class="flex wrap gap-8">
				<div v-for="(dateGroup, i) in selectedDatesGrouped" :key="`dateGroup${i}`" :class="{'can-remove': selectedDatesGrouped.length > 1 }" class="tag-v2 flex gap-8 rel">
					<template v-if="dateGroup.isGroup">
						<span class="no-shrink">{{ getDateGroupSpan(dateGroup) }}</span>
						<button v-if="selectedDatesGrouped.length > 1" class="reset remove" @click="removeDateGroup(dateGroup)">
							<icon-x class="actual" />
						</button>
					</template>
					<template v-else>
						<span class="no-shrink">{{ formatDate(dateGroup.dates[0].timestamp) }}</span>
						<button v-if="selectedDates.length > 1" class="reset remove" @click="removeDateGroup(dateGroup)">
							<icon-x class="actual" />
						</button>
					</template>
				</div>
			</div>

			<div v-else class="flex wrap gap-8">
				<div v-for="date in selectedDates" :key="date.timestamp" :class="{'can-remove': selectedDates.length > 1 }" class="tag-v2 flex gap-8 rel">
					<Tooltip v-if="date.holiday" arrow="top center">{{ date.holiday }}</Tooltip>
					<div v-if="date.holiday" class="round bg-aqua no-shrink" style="width:7px;height:7px;"></div>
					<span class="no-shrink">{{ formatDate(date.timestamp) }}</span>
					<button v-if="selectedDates.length > 1" class="reset remove" @click="removeDate(date)">
						<icon-x class="actual" />
					</button>
				</div>
			</div>
		</div>

		<!-- Status -->
		<div class="px-24 py-20 border">
			<h2 class="fs-16 lh-20 mb-8">Status</h2>
			<div class="toggle" style="width:250px">
				<button :class="['reset', { 'is-active': status == 'available' }]" @click="status = 'available'">
					<span>Available</span>
				</button>
				<button :class="['reset', { 'is-active': status == 'unavailable' }]" @click="status = 'unavailable'">
					<span>Unavailable</span>
				</button>
				<div class="slide"></div>
			</div>
			
			<div class="mt-24 fs-14 lh-16 flex" v-if="willBeMarkedAvailable.length">
				<span class="rel" style="margin-right:7px">
					<svg xmlns="http://www.w3.org/2000/svg" class="block" width="20" height="20" fill="none" viewBox="0 0 20 20"><path stroke="#004ADB" stroke-miterlimit="10" stroke-width="1.25" d="M10 17.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z"/><path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25" d="M10 6.25v4.375"/><path fill="#004ADB" d="M10 14.375a.937.937 0 1 0 0-1.875.937.937 0 0 0 0 1.875Z"/></svg>
					<Tooltip arrow="bottom center">TODO</Tooltip>
				</span>{{ willBeMarkedAvailable.join(' and ') }} will be changed from “Unavailable” to “Available”.
			</div>
			<div class="mt-24 fs-14 lh-16 flex gap-4" v-if="willBeMarkedUnavailable.length">
				<span class="rel" style="margin-right:7px">
					<svg xmlns="http://www.w3.org/2000/svg" class="block" width="20" height="20" fill="none" viewBox="0 0 20 20"><path stroke="#004ADB" stroke-miterlimit="10" stroke-width="1.25" d="M10 17.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z"/><path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25" d="M10 6.25v4.375"/><path fill="#004ADB" d="M10 14.375a.937.937 0 1 0 0-1.875.937.937 0 0 0 0 1.875Z"/></svg>
					<Tooltip arrow="bottom center">TODO</Tooltip>
				</span> {{ willBeMarkedUnavailable.join(' and ') }} will be changed from “Available” to “Unavailable”.
			</div>

			<div v-if="status == 'unavailable'" class="mt-24">
				<div class="flex gap-16 mb-8">
					<label for="" class="fs-14 lh-18 fw-500">Notes</label>
					<span class="fs-12 op-6">Optional</span>
				</div>
				<textarea v-model="notes"></textarea>
			</div>
		</div>

		<div v-if="status == 'available'" class="px-24 py-20 border flex wrap gap-x-56 gap-y-16">
			<div style="max-width:240px;">
				<h2 class="fs-16 lh-20 mb-8">Location</h2>
				<p class="fs-14 m0">Enter your location's {{ zipOrPostal }} code.</p>
			</div>
			<div>
				<PostalCodeField v-model="withinZip" no-label :country="$activeUser.profile.country" style="width:190px;" required>
					<template #unit>{{ zipOrPostal == 'ZIP' ? 'ZIP' : 'Postal Code' }}</template>
				</PostalCodeField>
			</div>
		</div>

		<!-- Available as -->
		<div v-if="status == 'available'" class="px-24 py-20 border">
			<h2 class="fs-16 lh-20 mb-8">Available As</h2>
			<div class="flex wrap" style="gap:10px;">
				<div v-for="role in availableRoles" class="role-checkbox checkbox-wrap" :key="`role${role.id}`">
					<input :id="`role${role.id}`" type="checkbox" v-model="selectedRoles" :value="role.id" />
					<label :for="`role${role.id}`">
						<div class="box">
							<icon-check-slim />
						</div>
						{{ role.name }}
					</label>
				</div>
			</div>
		</div>

		<!-- Role settings -->
		<div class="px-24 py-20 border" v-for="(roleSettings, i) in selectedRolesSettings" :key="`roleSettings${i}`">
			<h2 class="fs-16 lh-18 mb-16">{{ getRoleFromId(roleSettings.positionId).name }} Details</h2>
			
			<!-- Rate -->
			<div>
				<label class="fw-500 fs-14 color-violet mb-8 gap-4 flex" for="">
					{{ getRoleFromId(roleSettings.positionId).name }} Rate
					<span class="rel">
						<svg class="block" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
							<path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" />
							<path fill="#140047" d="M7.5 7.5H8V11h.5" />
							<path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H8V11h.5" />
							<path fill="#140047" d="M8 6a.75.75 0 1 0 0-1.5A.75.75 0 0 0 8 6Z" />
						</svg>
						<Tooltip arrow="bottom center">TODO</Tooltip>
					</span>
				</label>
				<div class="flex gap-16">
					<NumberField v-model="roleSettings.ratePerHour" type="USD" min="0" required style="width:150px;">
					</NumberField>
					<span class="fs-14 fw-500 color-violet">per hour</span>
				</div>
				<p v-if="roleSettings.positionId == 2 && (roleSettings.ratePerHour > 35 || roleSettings.ratePerHour < 15)" class="fs-12 lh-14">
					The industry standard rate for Assistants is $15-$35/hour based on experience.
				</p>
				<p v-if="roleSettings.positionId == 3 && (roleSettings.ratePerHour > 70 || roleSettings.ratePerHour < 35)" class="fs-12 lh-14">
					The industry standard rate for Second Shooters is $35-$70/hour based on experience.
				</p>
				<p v-if="roleSettings.positionId == 4 && (roleSettings.ratePerHour > 130 || roleSettings.ratePerHour < 75)" class="fs-12 lh-14">
					The industry standard rate for Lead Shooters is $75-$130/hour based on experience.
				</p>
			</div>

			<!-- Radius -->
			<div class="mt-32">
				<label class="fw-500 fs-14 color-violet mb-8 gap-4 flex" for="">
					Availability Within
					<span class="rel">
						<svg class="block" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
							<path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" />
							<path fill="#140047" d="M7.5 7.5H8V11h.5" />
							<path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H8V11h.5" />
							<path fill="#140047" d="M8 6a.75.75 0 1 0 0-1.5A.75.75 0 0 0 8 6Z" />
						</svg>
						<Tooltip arrow="bottom center">TODO</Tooltip>
					</span>
				</label>
				<div class="radius-inputs flex gap-16">
					<DistanceField v-model="roleSettings.withinMiles" min="0" max="150" required style="max-width:190px;" />
					<div class="fs-14 fw-500 color-violet">of {{ withinZip }}</div>
				</div>
			</div>
		</div>

		<!-- todo: make global component? -->
		<div v-if="errorMessage || successMessage" :class="{'error-message': errorMessage, 'success-message': successMessage}" class="mt-8">
			{{ errorMessage ? errorMessage : successMessage }}
		</div>

		<div class="flex-right wrap gap-16 mt-8">
			<button class="btn btn-large" @click="$emit('close')">Cancel</button>
			<button :disabled="!canSave" :class="{'is-processing': isProcessing }" class="btn btn-green btn-large" @click="onSave">
				Save
			</button>
		</div>
	</div>
</template>

<script>
import helpers from '@/mixins/helpers';
import formHelpers from '@/mixins/formHelpers';
import { request } from '@/plugins/axios';
import { DateTime } from 'luxon';

export default {
	name: 'AvailabilityEdit',
	mixins: [helpers, formHelpers],
	components: {
	},
	props: {
		context: {
			type: [String, null],
			default: null,
		},
	},
	data() {
		return {
			withinZip: '',
			status: 'available',
			selectedRoles: [],
			settings: [],
			showGroupedDates: true,
			notes: '',
		};
	},
	computed: {
		zipOrPostal() {
			return this.$activeUser.profile.country == 'USA' ? 'ZIP' : 'Postal';
		},
		canSave() {
			return (this.status == 'unavailable' || this.selectedRolesSettings.length);
		},
		selectedDates() {
			let dates = [...this.$store.getters['availability/selectedDates']]; 
			return dates.sort((a, b) => {
				return a.timestamp < b.timestamp ? -1 : 1;
			});
		},
		selectedDatesGrouped() {
			const out = [];
			let hasStartedGroup = false;
			let currentGroup = {
				isGroup: false,
				dates: [],
			}
			
			this.selectedDates.forEach((date, index) => {
				const timestamp = date.timestamp;
				const nextDate = this.selectedDates[index + 1];
				
				// Is the next selected date adjacent?
				if (nextDate && nextDate.timestamp - timestamp == 86400000) {
					hasStartedGroup = true;
					currentGroup.isGroup = true;
					currentGroup.dates.push(date);
				// Last adjacent day
				} else if (hasStartedGroup) {
					hasStartedGroup = false;
					currentGroup.dates.push(date);
					out.push(currentGroup);
					
					// Reset
					currentGroup = {
						isGroup: false,
						dates: [],
					}
				} else {
					currentGroup.isGroup = false;
					currentGroup.dates.push(date);

					// Push single date group
					out.push(currentGroup);

					// Reset
					currentGroup = {
						isGroup: false,
						dates: [],
					}
				}
			});

			return out;
		},
		selectedDateData() {
			return this.$store.getters['availability/selectedDateData'];
		},
		selectedRolesSettings() {
			return this.settings.filter(obj => this.selectedRoles.includes(obj.positionId));
		},
		dateList() {
			const dateList = [];
			this.selectedDates.forEach(obj => {
				dateList.push(obj.dateString);
			});

			return dateList;
		},
		selectedAvDates() {
			return this.selectedDates.filter(date => date.availabilityRates.length);
		},
		selectedUnDates() {
			return this.selectedDates.filter(date => !date.availabilityRates.length);
		},
		willBeMarkedAvailable() {
			const out = [];
			if (this.status == 'unavailable') return out;

			if (this.selectedAvDates.length && this.selectedUnDates.length) {
				this.selectedUnDates.forEach(date => {
					out.push(this.formatDate(date.timestamp));
				});
			}
			return out;
		},
		willBeMarkedUnavailable() {
			const out = [];		
			if (this.status == 'available') return out;

			if (this.selectedAvDates.length && this.selectedUnDates.length) {
				this.selectedAvDates.forEach(date => {
					out.push(this.formatDate(date.timestamp));
				});
			}

			return out;
		},
		hasGroupedDates() {
			return this.selectedDatesGrouped.filter(obj => obj.isGroup).length;
		},
	},
	methods: {
		getDateGroupSpan(dateGroup) {
			const firstDate = dateGroup.dates[0];
			const lastDate = dateGroup.dates[dateGroup.dates.length - 1];
			const firstMonthDay = DateTime.fromMillis(firstDate.timestamp).toLocaleString({ month: 'long', day: 'numeric' });
			const lastMonthDay = DateTime.fromMillis(lastDate.timestamp).toLocaleString({ month: 'long', day: 'numeric' });
			const lastDay = DateTime.fromMillis(lastDate.timestamp).toLocaleString({ day: 'numeric' });
			const lastYear = lastDate.dateString.substring(0,4);

			// Same month?
			if (firstDate.dateString.substring(5,7) == lastDate.dateString.substring(5,7)) {
				return `${firstMonthDay}-${lastDay}, ${lastYear}`;
			} else {
				return `${firstMonthDay} - ${lastMonthDay}, ${lastYear}`;
			}
		},
		removeDate(date) {
			this.$store.commit('availability/toggleSelectedDate', date);
		},
		removeDateGroup(dateGroup) {
			dateGroup.dates.forEach(date => {
				this.$store.commit('availability/toggleSelectedDate', date);
			});
		},
		initDefaults() {
			const myZip = this.$activeUser.profile.zip;
			this.withinZip = myZip;

			this.settings.push({
				positionId: 4,
				ratePerHour: 75,
				withinMiles: 100,
			});

			this.settings.push({
				positionId: 3,
				ratePerHour: 45,
				withinMiles: 100,
			});

			this.settings.push({
				positionId: 2,
				ratePerHour: 15,
				withinMiles: 100,
			});
		},
		initWithData() {
			this.selectedDateData[0].availableAs.forEach(role => {
				this.selectedRoles.push(role.positionId);
				let index = this.settings.findIndex(d => d.positionId == role.positionId);
				this.settings[index].ratePerHour = role.ratePerHour;
				this.settings[index].withinMiles = role.withinMiles;
			});

			this.withinZip = this.selectedDateData[0].withinZip;
		},
		onSave() {
			if (this.isProcessing) return;
			this.isProcessing = true;
			const payload = {
				dateList: this.dateList,
				notes: this.notes,
				availableAs: this.selectedRolesSettings,
				status: this.status,
				withinZip: this.withinZip,
			};

			request({
				url: '/availabilities/v2/dates/multiple',
				method: 'put',
				data: payload,
			}).then(() => {
				this.refreshAvailability();
				this.onSuccess();
			}).catch((error) => {
				this.isProcessing = false;
				this.errorMessage = this.getErrorMessage(error);
			});
		},
		onSuccess() {
			this.isProcessing = false;
			if (this.context == 'single') {
				this.flashSuccess('Your availability has been saved.', 1000, 'close');
			} else {
				this.flashSuccess('Your availability has been saved.', 1000);
				this.$emit('close');
			}
		},
	},
	created() {
		this.initDefaults();
		
		if (this.selectedDateData.length) {
			this.initWithData();
		}
	},
	watch: {
		status(status) {
			if (status == 'unavailable') {
				this.selectedRoles = [];
			}
		}
	},
	destroyed() {
		this.$store.commit('availability/clearSelectedDates');
	}
};
</script>
